import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Button, Drawer, ToggleButton, ToggleButtonGroup } from '@mui/material';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import CustomTableGrid from 'components/uiComponents/CustomTableGrid';
import FormikControl from 'components/wrapperComponents/FormikControl';
import { msgTypes } from 'constants/msgTypes';
import { Form, Formik } from 'formik';
import { InvoiceManagementModel } from 'model/screensModels/InvoiceManagementModel';
import { PaginatorModel } from 'model/utilModels/PaginatorModel';
import { ResponseModel } from 'model/utilModels/ResponseModel';
import React, { useEffect, useMemo, useState } from 'react';
import { LocalStorageService } from 'services/auth/LocalStorageService';
import { InvoiceManagementService } from 'services/menu/invoiceManagementService/InvoiceManagementService';
import { conformationAlert } from 'services/shared/alert';
import DisplayParticularInvoiceAsDrawer from './DisplayParticularInvoiceAsDrawer';

interface MyInvoiceProps {
  setFlag: (flag: boolean) => void;
  updateRecord: (record: InvoiceManagementModel) => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}


const MyInvoice: React.FC<MyInvoiceProps> = ({ setFlag, updateRecord }) => {
  const [myInvoiceList, setMyInvoiceList] =  useState<ResponseModel | null>(null); 
  const [selectedInvoice, setSelectedInvoice] = React.useState(new InvoiceManagementModel());
  const [open, setOpen] = React.useState(false);
  const [exportAsExcel, setExportAsExcel] = useState({})
  const [pagination, setPagination] = useState({
    pageSize: new PaginatorModel().pageSize,
    pageNumber: new PaginatorModel().pageNumber,
    filter: "",
    orderBy: "",
    desc: "",
  });
  const [activeTab, setActiveTab] = useState(0);
  const [status, setStatus] = React.useState(msgTypes.INVOICE_STATUS.PAID);
 // const exportAsExcel = {fileName:"InwardInvoices.xlsx", data:[] }

  const columns = useMemo(() =>
    [
      {
        header: "View",
        Cell: ({ renderedCellValue, row }) => (
          <Box sx={{ display: "flex", alignItems: "center"}}>
            <Button onClick={toggleDrawer(true, row.original)} ><span className="mt-2 badge badge-sm bg-gradient-warning">View</span></Button>
          </Box>
        ),
        size: 50,
      },
      {
        accessorKey: "invoiceId",
        header: "Invoice Number",
        size: 100
      },
      {
        accessorKey: "toCompanyName",
        header: "To Company Name",
        size: 200
      },
      {
        accessorKey: "amount",
        header: "Amount",
        size: 80,
        Cell: ({ row }) => {
          const formattedAmount = new Intl.NumberFormat('en-IN', {
            maximumFractionDigits: 0
          }).format(row.original.amount);
          return <span>{formattedAmount}</span>;
        }
      },
      {
        accessorKey: "currency",
        header: "Currency",
        size: 80
      },
      {
        accessorKey: "subject",
        header: "Subject",
        size: 150
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 120,
        Cell: ({ row }) => {
          const status = row.original.status;
          let statusStyle = {};
          let statusText = ''; 
          
          switch (status) {
            case 'Paid':
              statusStyle = { color: '#4cae4c', fontWeight: 'bold' };
              statusText = '✔ Paid';
              break;
            case 'Overdue':
              statusStyle = { color: '#d43f3a', fontWeight: 'bold' };
              statusText = '⚠ Overdue';
              break;
            case 'Outstanding':
              statusStyle = { color: '#ec971f', fontWeight: 'bold' };
              statusText = '⏳ Outstanding';
              break;
            default:
              statusStyle = { color: 'grey' }; 
              statusText = status;
          }
          
          return (
            <span style={statusStyle}>
              {statusText}
            </span>
          );
        }
      },
      {
        accessorKey: "uploadingDate",
        header: "Invoice Uploading Date",
      },
    
      {
        header: 'Action',
        Cell: ({ renderedCellValue, row }) => (
          <div className='d-flex'>
   
          <Tooltip title="Edit">
              <EditIcon fontSize="small" onClick={() => { editRecord(row.original) }} />
            </Tooltip>
            <Tooltip title="Delete">
              <DeleteIcon fontSize="small" onClick={() => { deleteRecord(row.original) }} />
            </Tooltip>
          </div>
        ),
        size: 10,
        grow: false,
      },


    ], []);

  

  useEffect(() => {
    getInvoiceList();
  }, [activeTab, pagination.pageNumber, pagination.pageSize, pagination.filter, pagination.orderBy, pagination.desc])

  useEffect(() => {
    if (open === false) {
      getInvoiceList();
    }
  }, [open])

  const prepareRequest = async () => {
    const requestBody = new InvoiceManagementModel();
    requestBody.pageNumber = pagination.pageNumber;
    requestBody.pageSize = pagination.pageSize;
    requestBody.filter = pagination.filter;
    requestBody.orderBy = pagination.orderBy;
    requestBody.desc = pagination.desc;

    if (activeTab === 0) {
      requestBody.status = msgTypes.INVOICE_STATUS.PAID
    } else if (activeTab === 1) {
      requestBody.status = msgTypes.INVOICE_STATUS.OUTSTANDING
    } else {
      requestBody.status = msgTypes.INVOICE_STATUS.OVERDUE
    }

    const userDetails = JSON.parse(await LocalStorageService.getUserDetails());
    requestBody.fromCompanyId = userDetails.companyId;

    return requestBody;
  };

  const getInvoiceList = async () => {
    const requestBody = await prepareRequest();
    try {
      const res = await InvoiceManagementService.getInvoiceList(requestBody);
      let filterData = undefined;
      if (res.statusMsg === msgTypes.SUCCESS.msg) {
        if(res.data!==''){
          setMyInvoiceList(res);
          filterData =res.data.tbody.map((data:InvoiceManagementModel)=>{
            return {
                "Invoice Id": data.invoiceId,
                "To Company Name": data.toCompanyName,
                "Amount": data.amount,
                "Currency": data.currency,
                "Status": data.status,
                "Subject": data.subject,
                "Uploading Date": data.uploadingDate
            }  
        })
        setExportAsExcel({fileName:"OutwardInvoices.xlsx", data:filterData})
       }else{
          setMyInvoiceList(new ResponseModel());
          setExportAsExcel({fileName:"OutwardInvoices.xlsx", data:[]})
        }
      }
    } catch (error) {
      console.error('Failed to fetch invoice list:', error);
    }
  };


  const editRecord = (record: InvoiceManagementModel) => {
    updateRecord(record);
    setFlag(true);
  };

  const deleteRecord = async (record: InvoiceManagementModel) => {
    const requestData: InvoiceManagementModel = new InvoiceManagementModel();
    requestData.invoiceId = record.invoiceId;

    const deleteFlag = await conformationAlert(msgTypes.CONFORMATION_MESSAGE.DELETE_RECORD, msgTypes.CONFORMATION_ICON.ERROR, "delete");
    if (deleteFlag) {
      const res = await InvoiceManagementService.deleteInvoice(requestData);
      if (res.statusMsg === msgTypes.SUCCESS.msg) {
        getInvoiceList();
      }
    }
  };

  const handleTabChange = (index: number) => {
    setActiveTab(index);
  };

  const toogleHandleChange = (
    event: React.MouseEvent<HTMLElement>,
    newStatus: string,
  ) => {
    setStatus(newStatus);
  };

  const initialValues = {};
  const onSubmit = () => {
    getInvoiceList();
  };

  const toggleDrawer = (newOpen: boolean, invoice: InvoiceManagementModel) => () => {
    setSelectedInvoice(invoice)
    setOpen(newOpen);
  };

  

  return (
    <>
      <ToggleButtonGroup
        color="primary"
        value={status}
        exclusive
        onChange={toogleHandleChange}
        aria-label="Platform"
         className='but---'
      >
        <ToggleButton className='invoice-button button-success' value={msgTypes.INVOICE_STATUS.PAID} onClick={() => handleTabChange(0)}>{msgTypes.INVOICE_STATUS.PAID}</ToggleButton>
        <ToggleButton className='invoice-button button-warning' value={msgTypes.INVOICE_STATUS.OUTSTANDING} onClick={() => handleTabChange(1)}>{msgTypes.INVOICE_STATUS.OUTSTANDING}</ToggleButton>
        <ToggleButton className='invoice-button button-danger' value={msgTypes.INVOICE_STATUS.OVERDUE} onClick={() => handleTabChange(2)}>{msgTypes.INVOICE_STATUS.OVERDUE}</ToggleButton>
      </ToggleButtonGroup>

      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={onSubmit}
      >
        {formik => (
          <div className={`invoice-container ${status === msgTypes.INVOICE_STATUS.PAID ? 'paid-section' : 
            status === msgTypes.INVOICE_STATUS.OUTSTANDING ? 'outstanding-section' : 
            status === msgTypes.INVOICE_STATUS.OVERDUE ? 'overdue-section' : ''}`}>
          <Form>
          {myInvoiceList ? (
            <CustomTableGrid
              item={
                <FormikControl
                  control='materialTable'
                  tableColumn={columns}
                  tableData={myInvoiceList}
                  // formikValue={formik}
                  setPagination={setPagination}
             
                  exportExcel={exportAsExcel}
                />
              }
            />
          ) : null}
          </Form>
          </div>
        )}
      </Formik>
      <Drawer open={open} anchor="right" onClose={toggleDrawer(false, new InvoiceManagementModel())} >
        <DisplayParticularInvoiceAsDrawer selectedInvoice={selectedInvoice} setOpen={setOpen} />
      </Drawer>
    </>
  );
};

export default MyInvoice;
